import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X } from 'lucide-react';
import Avatar from '@/components/comms/Avatar';
import type { Person } from '@/utils/commsHelpers';

interface Props {
  open: boolean;
  people: Person[];
  excludeIds?: string[];
  onClose: () => void;
  onPick: (personId: string) => void;
}

export default function PersonPicker({
  open,
  people,
  excludeIds = [],
  onClose,
  onPick,
}: Props) {
  const [query, setQuery] = useState('');
  const exclude = useMemo(() => new Set(excludeIds), [excludeIds]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return people
      .filter((p) => !exclude.has(p.id))
      .filter((p) => p.id !== 'system-bot' && p.id !== 'system-ai-voice')
      .filter((p) => !q || p.displayName.toLowerCase().includes(q) || p.role.toLowerCase().includes(q))
      .slice(0, 12);
  }, [query, people, exclude]);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-overlay/80 backdrop-blur-sm flex items-start justify-center pt-32 px-6"
          onClick={onClose}
          data-testid="comms-person-picker"
        >
          <motion.div
            initial={{ y: -8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -8, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md panel p-0 overflow-hidden"
          >
            <header className="flex items-center justify-between px-4 py-3 border-b border-gold-dim/15">
              <div>
                <p className="label-small-caps text-gold-bright">New direct message</p>
                <p className="font-serif text-cream-soft text-xs mt-1">
                  Pick who to message. Group DMs supported — pick one to start.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="btn-quiet p-1"
                aria-label="Close"
              >
                <X size={12} />
              </button>
            </header>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gold-dim/15">
              <Search size={12} className="text-cream-faint" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name or role…"
                className="flex-1 bg-transparent text-sm text-cream focus:outline-none"
                data-testid="comms-person-picker-input"
              />
            </div>
            <ul className="max-h-80 overflow-y-auto">
              {matches.length === 0 ? (
                <li className="px-4 py-3 font-serif text-cream-soft text-sm">
                  No matches.
                </li>
              ) : null}
              {matches.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => onPick(p.id)}
                    className="w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-ink-soft/60 transition"
                    data-testid={`comms-pick-${p.id}`}
                  >
                    <Avatar person={p} size="md" />
                    <span className="flex-1">
                      <span className="block font-inter text-sm text-cream">
                        {p.displayName}
                      </span>
                      <span className="block font-inter text-[9px] uppercase tracking-widest text-cream-faint mt-0.5">
                        {p.role}
                        {p.studioId ? ` · ${p.studioId.replace('-', ' ')}` : ''}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
